/**
 * Closing Modules — table list, identical layout convention to
 * AdminModulesProperty and AdminModulesUnderwriting.
 *
 * Row click expands inline to show ModuleNotes only. The 10-step closing
 * UI itself lives on the borrower profile's Closings tab (rendered by
 * <ClosingModule borrower={...} onClose={...} />); the "View →" button
 * deep-links there.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import ModuleNotes from '../components/admin/ModuleNotes';
import { STEPS, deriveCurrentStep } from '../components/admin/ClosingModule';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Stages the backend's qualifying-stages WHERE clause currently includes
// (backend/routes/closingModule.js). 'closing_disclosures' was dropped on
// the backend in commit c9bfd7d — keep this list in lockstep.
const QUALIFYING_STAGES = [
  'option_period', 'under_contract', 'closing', 'clear_to_close',
];
const URGENT_STAGES = ['closing', 'clear_to_close'];

// Closing dashboard sorts by SOONEST close date first (set on the
// backend via COALESCE(ca.closing_date, cms.step9_closing_date,
// u.date_closing) ASC NULLS LAST). Stage-rank sort was dropped — the
// upcoming close date is the more actionable axis on this dashboard
// since every row already filters to in-flight closing stages. Property
// + Underwriting dashboards keep their pipeline-rank sort because
// they don't have a single shared "due date" axis.

const STAGE_LABELS = {
  option_period:  'Option Period',
  under_contract: 'Under Contract',
  closing:        'Closing',
  clear_to_close: 'Clear to Close',
};

const FILTERS = [
  { value: 'all',    label: 'All' },
  { value: 'urgent', label: 'Urgent' },
  ...QUALIFYING_STAGES.map(s => ({ value: s, label: STAGE_LABELS[s] || s })),
];

// Pure: derive the step label for a list row. The backend already returns
// a flat row with the columns deriveCurrentStep needs — pass it as both
// dcp/cms/cd/op/user (the deriver only reads field names, not nesting).
function rowStep(b) {
  const step = deriveCurrentStep({
    dcp: b, closingModuleSteps: b, closingDisclosure: b, optionPeriod: b, user: b,
  });
  if (step === 'complete') return { num: STEPS.length, total: STEPS.length, label: 'Complete', isComplete: true };
  const def = STEPS.find(s => s.id === step);
  return {
    num: step,
    total: STEPS.length,
    label: def ? `Step ${step} — ${def.shortLabel}` : `Step ${step}`,
    isComplete: false,
  };
}

export default function AdminModulesClosing() {
  const navigate = useNavigate();
  const [borrowers, setBorrowers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('doorly_admin_token');
    axios.get(`${API}/api/admin/modules/closing`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setBorrowers(r.data || []))
      .catch(err => {
        const status = err.response?.status;
        const msg = err.response?.data?.error || err.message;
        setFetchError(status ? `API ${status}: ${msg}` : msg);
        setBorrowers([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // Sort state. Default (sortKey=null) → backend's soonest-close-date
  // ORDER BY stays canonical. Clicking a column header cycles
  // asc → desc → off so a user can return to the default ordering
  // without leaving the page.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc');
  };
  const sortIcon = (key) => {
    if (sortKey !== key) return '⇅';
    return sortDir === 'asc' ? '▲' : '▼';
  };

  // Client-side filter. The backend returns every qualifying-stage
  // borrower in one call already sorted by soonest close date (see
  // closingModule.js's ORDER BY COALESCE(...) ASC NULLS LAST); pills +
  // search narrow the list here.
  const term = search.trim().toLowerCase();
  let filtered = (filter === 'all'
    ? borrowers
    : filter === 'urgent'
      ? borrowers.filter(b => URGENT_STAGES.includes(b.application_status))
      : borrowers.filter(b => b.application_status === filter))
    .filter(b => !term
      || `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase().includes(term)
      || (b.loan_number || '').toLowerCase().includes(term)
      || (b.email || '').toLowerCase().includes(term));

  // Column-level sort. Accessors return a comparable value per column;
  // null/missing data sinks to the bottom regardless of sort direction
  // via Infinity / -Infinity sentinels so empty rows don't crowd the
  // top on an asc sort.
  if (sortKey) {
    const accessors = {
      borrower:  (b) => `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase(),
      stage:     (b) => QUALIFYING_STAGES.indexOf(b.application_status),
      days:      (b) => (b.days_in_stage == null ? null : Number(b.days_in_stage)),
      status:    (b) => {
        const st = rowStep(b);
        const total = st.total || 1;
        const passed = st.isComplete ? total : Math.max(0, st.num - 1);
        return total > 0 ? passed / total : 0;
      },
      step:      (b) => rowStep(b).num,
      closeDate: (b) => (b.close_date ? new Date(b.close_date).getTime() : null),
    };
    const accessor = accessors[sortKey];
    filtered = filtered.slice().sort((a, b) => {
      const va = accessor(a);
      const vb = accessor(b);
      const aN = va == null || Number.isNaN(va);
      const bN = vb == null || Number.isNaN(vb);
      if (aN && bN) return 0;
      if (aN) return 1;   // null/NaN always sinks
      if (bN) return -1;
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }

  const th = { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e5e7eb' };
  const thBtn = { background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4, textTransform: 'inherit', letterSpacing: 'inherit' };
  const td = { padding: '12px 14px', color: '#374151', fontSize: 13, verticalAlign: 'top' };
  const sortable = (label, key) => (
    <button type="button" onClick={() => toggleSort(key)} style={thBtn}>
      <span>{label}</span>
      <span style={{ fontSize: 9, color: sortKey === key ? '#3a6ad9' : '#9ca3af' }}>{sortIcon(key)}</span>
    </button>
  );

  return (
    <AdminLayout>
      <div style={{ padding: '24px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>Closing Modules</h1>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Active loans in the closing pipeline</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button key={f.value} onClick={() => setFilter(f.value)}
                style={{ padding: '5px 12px', borderRadius: 999, border: `1px solid ${filter === f.value ? '#3a6ad9' : '#e5e7eb'}`, background: filter === f.value ? '#eff6ff' : '#fff', color: filter === f.value ? '#3a6ad9' : '#374151', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by borrower name or loan #…"
          style={{ width: '100%', maxWidth: 360, padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, marginBottom: 16, boxSizing: 'border-box' }}
        />

        {fetchError && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca',
            color: '#991b1b', borderRadius: 10, padding: '12px 16px',
            marginBottom: 16, fontSize: 13,
          }}>
            <strong style={{ marginRight: 6 }}>Failed to load:</strong>{fetchError}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              <th style={th}>{sortable('Borrower',      'borrower')}</th>
              <th style={th}>{sortable('Stage',         'stage')}</th>
              <th style={th}>{sortable('Days in Stage', 'days')}</th>
              <th style={th}>{sortable('Module Status', 'status')}</th>
              <th style={th}>{sortable('Current Step',  'step')}</th>
              <th style={th}>{sortable('Close Date',    'closeDate')}</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {filtered.map((b, i) => {
                const isUrgent = URGENT_STAGES.includes(b.application_status);
                const expanded = expandedId === b.id;
                const days = b.days_in_stage;
                const overdue = (days ?? 0) > 14;
                const step = rowStep(b);
                const total  = step.total || 1;
                const passed = step.isComplete ? total : Math.max(0, step.num - 1);
                const pct    = total > 0 ? Math.round((passed / total) * 100) : 0;
                const sc = { bg: '#f3f4f6', text: '#374151', label: STAGE_LABELS[b.application_status] || b.application_status };
                return (
                  <React.Fragment key={b.id}>
                    <tr onClick={() => setExpandedId(expanded ? null : b.id)}
                        style={{ background: expanded ? '#f3f7ff' : i % 2 ? '#fafbfc' : '#fff', borderLeft: isUrgent ? '3px solid #f59e0b' : '3px solid transparent', cursor: 'pointer' }}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: '#111827', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span>{`${b.first_name || ''} ${b.last_name || ''}`.trim() || '—'}</span>
                          {/* Down-payment overdue alert — red ! pill. Fires
                              when on Step 2, B-C not received, >1 business
                              day since contract date. Computed backend-side
                              as b.dp_alert by the list endpoint. */}
                          {b.dp_alert && (
                            <span title="Down payment overdue — not received within 1 business day of contract date"
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 16, height: 16, background: '#ff082f', color: '#fff',
                                borderRadius: '50%', fontSize: 9, fontWeight: 700,
                                cursor: 'default', flexShrink: 0,
                              }}>!</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>{b.loan_number || `DLY-${b.id}`}</div>
                      </td>
                      <td style={td}>
                        <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.text }}>
                          {isUrgent ? '⚠ ' : ''}{sc.label}
                        </span>
                      </td>
                      <td style={{ ...td, color: overdue ? '#991b1b' : '#6b7280', fontWeight: overdue ? 600 : 400 }}>
                        {days == null ? '—' : `${days}d`}
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 70, height: 6, borderRadius: 999, background: '#e5e7eb', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#acf44e' : '#3a6ad9' }} />
                          </div>
                          <span style={{ fontSize: 12, color: '#374151' }}>{`${pct}% (${passed}/${total})`}</span>
                        </div>
                      </td>
                      <td style={td}><span style={{ fontWeight: 500, color: '#111827' }}>{step.label}</span></td>
                      <td style={td}>
                        {/* Close Date — composite of closing_appointments
                            .closing_date, closing_module_steps.step9_-
                            closing_date, and users.date_closing (backend
                            resolves the first non-null). Renders as a
                            short "Mon DD, YYYY"; falls back to em-dash
                            when nothing's scheduled yet. */}
                        {(() => {
                          const raw = b.close_date;
                          if (!raw) return <span style={{ color: '#9ca3af' }}>—</span>;
                          const d = new Date(raw);
                          if (isNaN(d)) return <span style={{ color: '#9ca3af' }}>—</span>;
                          // Past dates → red. Today / future → standard.
                          const today = new Date(); today.setHours(0,0,0,0);
                          const ymd = new Date(d); ymd.setHours(0,0,0,0);
                          const past = ymd.getTime() < today.getTime();
                          return (
                            <span style={{
                              fontSize: 12,
                              color: past ? '#991b1b' : '#111827',
                              fontWeight: past ? 600 : 500,
                            }}>
                              {d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                          {/* Notes count badge — mirrors AdminModulesProperty.
                              Blue + bold when there are notes, grey when zero. */}
                          {(() => {
                            const count = Number(b.notes_count) || 0;
                            return (
                              <span style={{
                                fontSize: 12,
                                color: count ? '#3a6ad9' : '#9ca3af',
                                fontWeight: count ? 600 : 400,
                              }}>
                                💬 {count}
                              </span>
                            );
                          })()}
                          <button onClick={e => { e.stopPropagation(); navigate(`/admin/borrowers/${b.id}?tab=closings`); }}
                            style={{ background: 'none', border: 'none', color: '#3a6ad9', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                            View →
                          </button>
                          <span style={{ color: '#9ca3af', fontSize: 11 }}>{expanded ? '▲' : '▼'}</span>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={7} style={{ padding: 0, borderLeft: isUrgent ? '3px solid #f59e0b' : '3px solid transparent' }}>
                          <div style={{ padding: '16px 20px', background: '#fbfcfd', borderTop: '1px solid #eef0f2' }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>Notes</div>
                            <div style={{ maxWidth: 640 }}>
                              <ModuleNotes moduleType="closing" borrowerId={b.id}
                                onCountChange={(n) => setBorrowers(prev =>
                                  prev.map(r => r.id === b.id ? { ...r, notes_count: n } : r)
                                )} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                  {borrowers.length === 0
                    ? 'No borrowers in closing stages'
                    : `No borrowers match the "${FILTERS.find(f => f.value === filter)?.label || filter}" filter`}
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
